import { supabase } from './supabase';
import { identifyUser, trackEvent } from './posthog';

/**
 * Persists onboarding data (score, answers) and links Paddle transaction
 * to the newly created user profile.
 * Reads from sessionStorage so it works both in OnboardingFlow and PostCheckout.
 */
export async function persistOnboarding(userId: string): Promise<void> {
  // Read onboarding score + answers from sessionStorage
  let score = 85;
  let answers: Record<string, unknown> = {};
  try {
    const raw = sessionStorage.getItem('atilum_onboarding');
    if (raw) {
      const data = JSON.parse(raw);
      if (data.score) score = data.score;
      if (data.answers) answers = data.answers;
    }
  } catch { /* ignore */ }

  identifyUser(userId);
  trackEvent('onboarding_completed', { score });

  const updatePayload = {
    fertility_score: score,
    onboarding_data: answers,
    onboarding_completed_at: new Date().toISOString(),
  };

  // Use update (not upsert) — the profile row is created by the handle_new_user
  // trigger on signup. Upsert fails because authenticated users lack INSERT on profiles.
  const doUpdate = () =>
    supabase.from('profiles').update(updatePayload).eq('id', userId);

  let { error } = await doUpdate();

  if (error) {
    // Retry once after 500ms (profile row may not exist yet due to trigger lag)
    await new Promise(r => setTimeout(r, 500));
    ({ error } = await doUpdate());
    if (error) console.error('persistOnboarding failed:', error);
  }

  // Seed the first score_history row so the progress chart has an initial data point
  await supabase.from('score_history').insert({
    user_id: userId,
    score,
    source: 'onboarding',
  });

  // Link Paddle customer if checkout happened before signup
  const paddleRaw = sessionStorage.getItem('atilum_paddle');
  if (paddleRaw) {
    try {
      const paddle = JSON.parse(paddleRaw);
      await supabase.rpc('link_paddle_customer', {
        p_transaction_id: paddle.transactionId,
        ...(paddle.customerId && { p_customer_id: paddle.customerId }),
      });
    } catch (e) {
      console.error('link_paddle_customer failed:', e);
    }
  }
}
